// Schedule-specific formatting + the route id parser. No DOM, no state.
import { SHORT_MONTHS } from "../../../shared/document-format.js";
import { escapeHtml } from "./format.js";
import { formatPence } from "./format.js";
import { scheduleSentence, ordinal } from "../../../shared/schedule-sentence.js";

export function getScheduleIdFromPath(path) {
  const match = String(path || "").match(/^\/schedules\/([^/]+)$/);
  if (!match) return "";
  try { return decodeURIComponent(match[1]); } catch { return match[1]; }
}

// `ordinal` moved with the sentence. Re-exported because the create form's
// live preview and the list both still call it by this name.
export { ordinal };

export function formatFrequencyLabel(frequency) {
  switch (frequency) {
    case "weekly": return "Weekly";
    case "biweekly": return "Every 2 weeks";
    case "monthly": return "Monthly";
    case "quarterly": return "Quarterly";
    case "annually": return "Annually";
    default: return frequency || "Monthly";
  }
}

export function formatScheduleStatus(status) {
  switch (status) {
    case "draft": return "Draft";
    case "active": return "Active";
    case "paused": return "Paused";
    case "ended": return "Ended";
    case "failed": return "Failed";
    default: return status || "";
  }
}

// The list's Next invoice for a schedule with no next run date: a draft
// hasn't been started, and an ended one has no more to make. Words rather
// than a dash (COPY.md), and not the status again, which the next column has.
export function noNextInvoiceWords(status) {
  return status === "draft" ? "Not started" : "None";
}

export function scheduleStatusTagClass(status) {
  // Reuse the shared status-tag colour classes (Figma Tag 13:35, defined in
  // base.css as `.app-invoice-status-*`) — there is no separate schedule
  // palette. The previous `ar-td-status-tag-*` names were never defined, so every
  // schedule tag fell back to the bare muted-grey base (active/paused/ended all
  // looked identical). active → green, paused/failed → orange, ended/draft → grey.
  switch (status) {
    case "active": return "app-invoice-status-paid";
    case "paused": return "app-invoice-status-overdue";
    case "failed": return "app-invoice-status-overdue";
    case "ended": return "app-invoice-status-draft";
    case "draft": return "app-invoice-status-draft";
    default: return "";
  }
}

export function buildScheduleSentence(schedule) {
  // The sentence moved to `shared/schedule-sentence.js` when the phone started
  // saying it too (#624). It answers TEXT; this call site escapes it, because
  // this one builds HTML and the shared module has no business knowing that.
  return escapeHtml(scheduleSentence(schedule));
}

export function buildLiveSentence({ contactName, totalPence, frequency, startDate, endDate, maxOccurrences, dueDaysAfter, deliveryMode, currency }) {
  return buildScheduleSentence({ contactName, totalPence, frequency, startDate, endDate, maxOccurrences, dueDaysAfter, deliveryMode, currency });
}

// ── Repeat (the web port of the iPhone's Repeat row, #1037) ───────────────
// One form for an invoice and a recurring invoice. Repeat reads as Calendar's
// "Repeat: Never": Never is an invoice, and any of the five cadences the
// server accepts (server/routes/schedules.js, VALID_FREQUENCIES) makes the
// same form a new recurring invoice. The words are formatFrequencyLabel's, as
// the recurring invoice screens word them.
export const REPEAT_NEVER = "never";
export const REPEAT_FREQUENCIES = ["weekly", "biweekly", "monthly", "quarterly", "annually"];

/** What Repeat offers. `offersNever` is false where only a recurring invoice can be made. */
export function repeatChoices({ offersNever = true } = {}) {
  return [
    ...(offersNever ? [{ value: REPEAT_NEVER, label: "Never" }] : []),
    ...REPEAT_FREQUENCIES.map((value) => ({ value, label: formatFrequencyLabel(value) }))
  ];
}

/** Whether a Repeat value is a cadence, which makes the form a recurring invoice. */
export function isRepeating(value) {
  return REPEAT_FREQUENCIES.includes(value);
}

/**
 * Whether the create screen shows Repeat, and whether Never is one of its
 * choices, for what it opened on. An invoice that comes from something stays
 * an invoice: a converted quote (the save flips the quote), one raised from a
 * job, and a release of retention (its parts are claimed by the save). An
 * interest invoice and a saved draft open on the edit screen, which has no
 * Repeat. Make recurring (`fromInvoice`) is a recurring invoice whatever is
 * chosen, so it offers the cadences without Never. A copy may become either.
 */
export function repeatRowFor({ fromQuote = false, fromJob = false, releasing = false, makingRecurring = false } = {}) {
  if (fromQuote || fromJob || releasing) return { offered: false, offersNever: true };
  return { offered: true, offersNever: !makingRecurring };
}

/**
 * The sentence under a new recurring invoice's dates: when it runs, from its
 * cadence and start date ("2026-10-14"). The month-end one matters most: a
 * monthly invoice starting on the 29th, 30th or 31st can't run on that day
 * every month, and somebody choosing it should find out now, not in February.
 * The same sentences as the iPhone's (ScheduleTimingNote).
 */
export function scheduleTimingSentence(frequency, startDate) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(startDate || ""));
  if (!match) return "";
  const d = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const day = d.getUTCDate();
  if (frequency === "monthly" || frequency === "quarterly") {
    if (day >= 29) return "Some months are shorter. On those months, the invoice will generate on the last day of that month instead.";
    return `This will run on the ${ordinal(day)} of every ${frequency === "quarterly" ? "3 months" : "month"}.`;
  }
  if (frequency === "weekly" || frequency === "biweekly") {
    const dayName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][d.getUTCDay()];
    return `This will run every${frequency === "biweekly" ? " other" : ""} ${dayName}.`;
  }
  if (frequency === "annually") {
    return `This will run once a year on ${MONTHS_LONG[d.getUTCMonth()]} ${ordinal(day)}.`;
  }
  return "";
}

const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MONTHS_SHORT = SHORT_MONTHS;


/**
 * The main button of a new recurring invoice: Start, with the day the first
 * invoice goes. A start on or before today runs today, as the recurring tick
 * runs anything due. Both days are "YYYY-MM-DD"; a later year is named.
 */
export function startButtonLabel(startDate, today) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(startDate || ""));
  if (!match || !(String(startDate) > String(today || ""))) return "Start today";
  const year = Number(match[1]);
  const shown = `${Number(match[3])} ${MONTHS_SHORT[Number(match[2]) - 1]}`;
  return `Start on ${String(today || "").slice(0, 4) === String(year) ? shown : `${shown} ${year}`}`;
}

// What the schedule's screen does with a refused action (30 October 2026).
// - "notice": not run, or not finished (409 ScheduleRunSkipped or
//   ScheduleStillSending). Nothing failed, so it is the server's sentence as a
//   notice over the schedule as it is now.
// - "redraw": Send now or Retry was tried and failed (422). The server has
//   written the failed run and marked the schedule Failed, with Retry, so the
//   page drawn before it is stale: drawn again, it says why in its own banner.
//   Left as it was, it read Active, with its next date and Send now still
//   offered, over a red line, until the page was opened again.
// - "error": anything else changed nothing, so the sentence goes under the
//   status on the page as it is.
export function afterScheduleActionRefused(path, err) {
  if (err?.status === 409 && ["ScheduleRunSkipped", "ScheduleStillSending"].includes(err?.body?.error)) return "notice";
  if (err?.status === 422 && (path === "run-now" || path === "retry")) return "redraw";
  return "error";
}
